import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Diff } from "@/features/FormCard/Diff";

import { AutocompleteField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { currencies } from "@/data/currencies";

import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";

const table_name = "a7_opcvm_strategies";

const FormSchema = z.object({
  contact_fonds: z.string().nullable().optional(),
  contact_afd: z.string().nullable().optional(),
  denomination_fonds: z.string().nullable().optional(),
  identifiant_fonds: z.string().nullable().optional(),
  devise_de_reporting: z.string().nullable().optional(),
});

export function A7OPCVMStrategiesEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Funds",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <InputField
                  name="denomination_fonds"
                  label={t(`${table_name}.columns.denomination_fonds`, {
                    ns: "tables",
                    defaultValue: "Dénomination",
                  })}
                  isRequired={true}
                  control={form.control}
                />

                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="identifiant_fonds"
                    label={t(`${table_name}.columns.identifiant_fonds`, {
                      ns: "tables",
                      defaultValue: "Identifiant",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <AutocompleteField
                    name="devise_de_reporting"
                    label={t(`${table_name}.columns.devise_de_reporting`, {
                      ns: "tables",
                      defaultValue: "Devise",
                    })}
                    isRequired={true}
                    options={currencies}
                    control={form.control}
                  />
                </div>
                <InputField
                  name="contact_afd"
                  label={t(`${table_name}.columns.contact_afd`, {
                    ns: "tables",
                    defaultValue: "Contact AFD",
                  })}
                  control={form.control}
                />
                <InputField
                  name="contact_fonds"
                  label={t(`${table_name}.columns.contact_fonds`, {
                    ns: "tables",
                    defaultValue: "Contact Fonds",
                  })}
                  control={form.control}
                />
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  elementId={id}
                  client="a7"
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={changedFields ? Diff(changedFields) : t("no_changes")}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
